/**
 * Shared helpers for the editor roots' action handlers. Both the content
 * editor and the schema editor go through identical motions for "POST to
 * /deploy" and "read a file, parse JSON, surface parse errors" — extracted
 * here so the two paths can't drift on error wording or response handling.
 */

import type { JsonSchema, JsonValue } from '@bliksys/shared';

import { toast } from './components/Toast.js';

const API_BASE = import.meta.env.BLIKSYS_API_URL ?? 'http://localhost:3001';

export interface DeployRequest {
  target: unknown;
  schema: JsonSchema;
  data: JsonValue;
}

/**
 * POST a deploy request to the API. Returns true when the deploy succeeded
 * (HTTP 2xx), false when the server rejected it (alert shown), and
 * 'unreachable' when the network call itself failed (toast shown).
 *
 * Caller decides whether to mark the store as saved — different editors
 * have different "what counts as saved" rules.
 */
export async function postDeploy(req: DeployRequest): Promise<true | false | 'unreachable'> {
  try {
    const res = await fetch(`${API_BASE}/deploy`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(req),
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { message?: string };
      toast(`Deploy failed: ${body.message ?? res.statusText}`, 'error');
      return false;
    }
    return true;
  } catch {
    toast('Deploy API unreachable; saved locally.', 'info');
    return 'unreachable';
  }
}

/**
 * Cloud-mode deploy. Hits the paid endpoint with just an org slug +
 * project id; the server resolves the deploy target, decrypts secrets,
 * and dispatches to the right adapter. The customer never sees the
 * credentials at request time — they were configured once at the
 * org level and reused.
 */
export async function postPaidDeploy(args: {
  orgSlug: string;
  projectId: string;
}): Promise<true | false | 'unreachable'> {
  try {
    const res = await fetch(
      `${API_BASE}/v1/orgs/${encodeURIComponent(args.orgSlug)}/projects/${encodeURIComponent(args.projectId)}/deploy`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      },
    );
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { code?: string; message?: string };
      const hint =
        body.code === 'NO_DEPLOY_TARGET'
          ? 'Set a deploy target in project settings first.'
          : (body.message ?? res.statusText);
      toast(`Deploy failed: ${hint}`, 'error');
      return false;
    }
    return true;
  } catch {
    toast('Deploy API unreachable.', 'info');
    return 'unreachable';
  }
}

/**
 * Read a file, parse it as JSON, and hand the result to the caller's
 * `accept` callback. Surfaces parse and read failures as toasts and
 * announces success as a toast — both editors agreed on this UX, so it
 * lives here rather than at each call site.
 *
 * `accept` returns true when the parsed value was acceptable. Returning
 * false suppresses the success toast (caller has already notified the
 * user about a shape rejection).
 */
export function readJsonFile(file: File, accept: (parsed: unknown) => boolean): void {
  void file
    .text()
    .then((text) => {
      let parsed: unknown;
      try {
        parsed = JSON.parse(text);
      } catch (err) {
        toast(
          `Could not parse ${file.name}: ${err instanceof Error ? err.message : 'invalid JSON'}`,
          'error',
        );
        return;
      }
      if (accept(parsed)) {
        toast(`Imported ${file.name}.`, 'success');
      }
    })
    .catch((err: unknown) => {
      toast(
        `Could not read ${file.name}: ${err instanceof Error ? err.message : 'unknown error'}`,
        'error',
      );
    });
}

/**
 * Trigger a client-side JSON file download. Used by both editors' Export
 * buttons and the landing page's per-row download actions — pure DOM,
 * no API call.
 */
export function downloadJson(value: unknown, filename: string): void {
  const blob = new Blob([JSON.stringify(value, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
